import React, { useState, useMemo, useEffect } from "react";
import { useTheme } from "@mui/material/styles";
import { v4 as uuidv4 } from "uuid";
import useDataSourceManager from "hooks/useDataSourceManager";
import mainStyle from "../../../mainStyle.module.scss";
import { StyledSelect } from "components/Select/searchWithSelect";
import { MenuSelect } from "components/Select/menuSelect";
import { ErrorNotification } from "pages/System/UserManagement/ErrorHandle";
import {
  Box,
  Button,
  Typography,
  Divider,
  TextField,
  CircularProgress,
} from "@mui/material";
import { SolidButton } from "components/buttons/SolidButton";
import { OutlineButton } from "components/buttons/OutlineButton";
import { OutlineRectangleButton } from "components/buttons/OutlineRectangleButton";
import ModalContainer from "components/ModalContainer";
import GlobalAlert from "components/Notification";

import { ReactComponent as UnverifiedIcon } from "assets/images/icons/icn_table_cell_Unverified.svg";
import { ReactComponent as AddIcon } from "assets/images/icons/icn_table_cell_Add.svg";
import { ReactComponent as AddHoverIcon } from "assets/images/icons/icn_table_cell_Add_hover.svg";
import { ReactComponent as CloseIcon } from "assets/images/icons/icn_close.svg";

const InviteUserModal = ({
  openInviteUserModal,
  setOpenInviteUserModal,
  allSitesName,
  orgRoleHierarchy,
  siteRoleHierarchy,
  currentUserOrgRoleId,
  entId,
  orgId,
  getListOrgUsers,
  getListPendingOrgUsers,
}) => {
  const [isAddHovered, setIsAddHovered] = useState(false);
  const [isEmailValid, setIsEmailValid] = useState(true);
  const [createUsersUuid, setCreateUsersUuid] = useState(uuidv4());
  const [errorMessageHandleModal, setErrorMessageHandleModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [actionModal, setActionModal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const initialData = [
    {
      email: "",
      orgRoleId: "",
      orgRoleName: "",
      siteAccesses: [],
    },
  ];
  const [alertInfo, setAlertInfo] = useState({
    open: false,
    severity: "",
    message: "",
  });

  const [data, setData] = useState(initialData);
  const theme = useTheme();
  const breadcrumbs = JSON.parse(localStorage.getItem("selectBreadcrumbs"));
  const hasManageOrg = breadcrumbs.realm?.hasManageOrg === true;
  const { dataMap, getData, decodeTokenData, sendRequest, sendBatch } =
    useDataSourceManager();

  const roleNameToIdMap = useMemo(() => {
    const map = new Map();
    orgRoleHierarchy.forEach((r) => map.set(r.orgRoleName, r.orgRoleId));
    return map;
  }, [orgRoleHierarchy]);

  const isValidInputStyles = (isValid) => ({
    width: "65%",
    "& .MuiOutlinedInput-root": {
      borderRadius: theme.shape.customBorderRadius[1],
      height: "31px",
      backgroundColor: theme.palette.primary.transparent,

      "& .MuiOutlinedInput-notchedOutline": {
        borderColor: isValid
          ? theme.palette.primary.borderGreyPrimary
          : theme.palette.primary.redPrimary,
      },

      "&:hover .MuiOutlinedInput-notchedOutline": {
        borderColor: isValid
          ? theme.palette.primary.mainHover
          : theme.palette.primary.redPrimary,
      },

      "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
        borderColor: isValid
          ? theme.palette.primary.main
          : theme.palette.primary.redPrimary,
      },
    },
  });

  const handleCloseModal = () => {
    setOpenInviteUserModal(false);
  };

  const isValidEmail = (email) => {
    return /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/.test(
      email.trim(),
    );
  };
  const handleEmailChange = (e) => {
    const value = e.target.value;
    setData((prev) => {
      const updated = [...prev];
      updated[0].email = value;
      return updated;
    });
    setIsEmailValid(isValidEmail(value));
  };

  const handleChange = (index, field, value) => {
    setData((prevData) => {
      const updated = [...prevData];
      const user = { ...updated[0] };

      if (field === "orgRole") {
        user.orgRoleName = value.orgRoleName;
        user.orgRoleId = value.orgRoleId;
        if (
          value?.orgRoleName === "Admin" ||
          value?.orgRoleName === "Super Admin"
        ) {
          user.siteAccesses = [];
        } else if (
          value?.orgRoleName === "Viewer" &&
          (!user.orgAccesses || user.orgAccesses.length === 0)
        ) {
          user.siteAccesses = [{ siteName: "", siteRoleName: "" }];
        }
      } else if (field === "site") {
        const siteAccesses = [...user.siteAccesses];
        siteAccesses[index] = {
          ...siteAccesses[index],
          siteId: value.siteId,
          siteName: value.siteName,
          siteRoleId: value.siteRoleId,
          siteRoleName: value.siteRoleName,
        };
        user.siteAccesses = siteAccesses;
      } else if (field === "role") {
        const siteAccesses = [...user.siteAccesses];
        siteAccesses[index] = {
          ...siteAccesses[index],
          siteRoleId: value.siteRoleId,
          siteRoleName: value.siteRoleName,
        };
        user.siteAccesses = siteAccesses;
      }

      updated[0] = user;
      return updated;
    });
  };

  const handleAddOrgAccess = () => {
    setData((prev) => {
      const updated = [...prev];
      updated[0].siteAccesses.push({
        siteId: "",
        siteName: "",
        siteRoleName: "",
        siteRoleId: null,
      });
      return updated;
    });
  };

  const handleDeleteOrgAccess = (index) => {
    setData((prev) => {
      const updated = [...prev];
      updated[0].siteAccesses.splice(index, 1);
      return updated;
    });
  };

  const isSaveDisabled = data.some((entry) => {
    if (!entry.email || !isValidEmail(entry.email) || !entry.orgRoleId) {
      return true;
    }

    if (entry.siteAccesses.length === 0 && entry.orgRoleId === 7) {
      return true;
    }

    return entry.siteAccesses.some(
      (access) => !access.siteId || !access.siteRoleId,
    );
  });

  // create new user
  const handleInviteUser = () => {
    const { email, orgRoleId, siteAccesses } = data[0];
    setIsLoading(true);

    const resourcePath =
      hasManageOrg && orgId
        ? "/ents/:entId/orgs/:orgId/invitations.create"
        : "/orgs/:orgId/invitations.create";

    const restfulParams = hasManageOrg && orgId ? { entId, orgId } : { orgId };

    const requestParams = {
      email,
      orgRoleId,
      siteAccesses,
    };

    sendRequest(resourcePath, createUsersUuid, restfulParams, requestParams);
  };

  useEffect(() => {
    const createUsersResponse = getData(createUsersUuid);
    if (!createUsersResponse) return;

    setIsLoading(false);

    if (createUsersResponse?.code === 500) {
      setErrorMessageHandleModal(true);
      setErrorMessage(createUsersResponse?.message);
      getListOrgUsers();
      getListPendingOrgUsers();
      setOpenInviteUserModal(false);
    } else if (createUsersResponse?.code === 400) {
      setActionModal(true);
      setErrorMessage(createUsersResponse?.message);
    } else if (createUsersResponse?.code === 405) {
      setAlertInfo({
        open: true,
        severity: "error",
        message: createUsersResponse?.message,
      });
    } else {
      getListOrgUsers();
      getListPendingOrgUsers();
      setOpenInviteUserModal(false);
    }
  }, [dataMap]);

  useEffect(() => {
    if (openInviteUserModal) {
      setData([
        {
          email: "",
          mspRoleId: "",
          mspRoleName: "",
          siteAccesses: [],
        },
      ]);
      setIsEmailValid(true);
      setErrorMessageHandleModal(false);
      setErrorMessage("");
    }
  }, [openInviteUserModal]);

  return (
    <>
      <ModalContainer display={openInviteUserModal} modalWidth="auto">
        {isLoading && (
          <Box
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              backgroundColor: "rgba(255,255,255,0.6)",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              zIndex: 9999,
            }}
          >
            <CircularProgress sx={{ color: theme.palette.primary.mainHover }} />
          </Box>
        )}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography
            sx={{
              mb: "30px",
              ml: "20px",
              color: theme.palette.primary.main,
              fontFamily: theme.typography.fontFamily,
              fontSize: theme.typography.customFontSize[5],
              fontWeight: theme.typography.customFontWeight[3],
            }}
          >
            Invite User
          </Typography>
          <Button
            onClick={handleCloseModal}
            sx={{
              marginRight: "-20px",
              marginTop: "-10px",
              backgroundColor: theme.palette.primary.transparent,
              boxShadow: "none",
              "&:hover": {
                backgroundColor: theme.palette.primary.transparent,
              },
            }}
          >
            <CloseIcon />
          </Button>
        </Box>

        <Box sx={{ display: "flex", gap: 2 }}>
          {/* Profile */}
          <Box sx={{ flex: 1, mb: 12 }}>
            <Box sx={{ marginLeft: "20px" }}>
              <Box sx={{ display: "flex", flexDirection: "column" }}>
                <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                  <Typography
                    className={mainStyle["user-management-modal-typography"]}
                  >
                    Email
                    <Typography
                      component="span"
                      sx={{ color: theme.palette.primary.redPrimary }}
                    >
                      *
                    </Typography>
                  </Typography>
                  <TextField
                    type="text"
                    value={data[0].email}
                    onChange={handleEmailChange}
                    placeholder="Enter email"
                    sx={isValidInputStyles(isEmailValid)}
                    error={!isEmailValid}
                  />
                </Box>
                {!isEmailValid && (
                  <Typography
                    sx={{
                      color: theme.palette.primary.redPrimary,
                      fontSize: "0.75rem",
                      marginLeft: "9rem",
                      marginTop: "-1rem",
                    }}
                  >
                    Invalid email address
                  </Typography>
                )}

                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <Typography
                    className={mainStyle["user-management-modal-typography"]}
                  >
                    Organization Role
                  </Typography>
                  <MenuSelect
                    value={data[0].orgRoleName}
                    onChange={(opt) => handleChange(null, "orgRole", opt)}
                    options={(() => {
                      if (
                        !currentUserOrgRoleId ||
                        typeof currentUserOrgRoleId !== "number"
                      )
                        return [];

                      return orgRoleHierarchy
                        .filter(
                          (role) => role.orgRoleId >= currentUserOrgRoleId,
                        )
                        .map((role) => ({
                          label: role.orgRoleName,
                          value: role.orgRoleName,
                          orgRoleId: role.orgRoleId,
                          orgRoleName: role.orgRoleName,
                        }));
                    })()}
                    width="100%"
                  />
                </Box>
              </Box>
            </Box>
          </Box>

          <Divider
            orientation="vertical"
            flexItem
            sx={{
              borderColor: theme.palette.primary.greyThread,
              borderWidth: 1,
            }}
          />

          {/* Site Privilege */}
          <Box sx={{ flex: 1 }}>
            <Box sx={{ marginLeft: "20px" }}>
              <Typography
                sx={{
                  color: theme.palette.primary.greyPrimary,
                  fontSize: theme.typography.customFontSize[1],
                  fontWeight: theme.typography.customFontWeight[1],
                  fontFamily: theme.typography.fontFamily,
                }}
              >
                Site Privilege
              </Typography>

              <Box
                sx={{
                  marginTop: "10px",
                  display: "flex",
                  gap: 2,
                }}
              >
                <Typography
                  sx={{
                    width: 177,
                    color: theme.palette.primary.greyThird,
                    fontSize: theme.typography.customFontSize[3],
                    fontWeight: theme.typography.customFontWeight[2],
                    fontFamily: theme.typography.fontFamily,
                  }}
                >
                  Site
                </Typography>
                <Typography
                  sx={{
                    width: 177,
                    color: theme.palette.primary.greyThird,
                    fontSize: theme.typography.customFontSize[3],
                    fontWeight: theme.typography.customFontWeight[2],
                    fontFamily: theme.typography.fontFamily,
                  }}
                >
                  Role
                </Typography>
              </Box>
              <Divider
                sx={{
                  marginTop: "10px",
                  borderColor: theme.palette.primary.greyThread,
                  borderWidth: 1,
                }}
              />
              <Box sx={{ mt: 2 }}>
                {data[0].orgRoleName === "Super Admin" ||
                data[0].orgRoleName === "Admin" ? (
                  <Box
                    sx={{
                      marginTop: "60px",
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      fontFamily: theme.typography.fontFamily,
                      fontSize: theme.typography.customFontSize[2],
                      color: theme.palette.primary.greyPrimary,
                    }}
                  >
                    All Sites
                  </Box>
                ) : (
                  <>
                    {data[0].siteAccesses.map((site, index) => (
                      <Box
                        key={index}
                        sx={{
                          display: "grid",
                          gridTemplateColumns:
                            index > 0 ? "44% 1fr auto" : "44% 56%",
                          mb: 2,
                          width: "100%",
                        }}
                      >
                        <Box sx={{ mr: 1 }}>
                          <StyledSelect
                            value={site.siteName}
                            onChange={(opt) => handleChange(index, "site", opt)}
                            options={allSitesName.map((s) => ({
                              ...s,
                              value: s.siteName,
                              label: s.siteId,
                            }))}
                            disabledOptions={data[0].siteAccesses
                              .filter((_, i) => i !== index)
                              .map((access) => access.siteName)}
                            placeholder="Select Site"
                            width="100%"
                          />
                        </Box>
                        <Box sx={{ mr: index > 0 ? 1 : 0 }}>
                          <MenuSelect
                            value={site.siteRoleName}
                            onChange={(opt) => handleChange(index, "role", opt)}
                            options={(() => {
                              if (!site.siteName) return [];

                              const matchedOrg = allSitesName.find(
                                (org) => org.siteName === site.siteName,
                              );

                              if (!matchedOrg) return [];

                              const maxRoleId = roleNameToIdMap.get(
                                matchedOrg.siteRoleName,
                              );

                              if (typeof maxRoleId !== "number") return [];

                              return siteRoleHierarchy
                                .filter((role) => role.siteRoleId >= maxRoleId)
                                .map((role) => ({
                                  label: role.siteRoleName,
                                  value: role.siteRoleName,
                                  siteRoleId: role.siteRoleId,
                                  siteRoleName: role.siteRoleName,
                                }));
                            })()}
                            placeholder="Select Role"
                            width="100%"
                          />
                        </Box>
                        {index > 0 && (
                          <Box
                            sx={{
                              width: 40,
                              display: "flex",
                              justifyContent: "center",
                            }}
                          >
                            <Button
                              onClick={() => handleDeleteOrgAccess(index)}
                              sx={{
                                padding: 0,
                                minWidth: 0,
                                backgroundColor:
                                  theme.palette.primary.transparent,
                                boxShadow: "none",
                              }}
                            >
                              <UnverifiedIcon />
                            </Button>
                          </Box>
                        )}
                      </Box>
                    ))}

                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "flex-end",
                        mt: 2,
                      }}
                    >
                      <OutlineRectangleButton
                        onClick={handleAddOrgAccess}
                        onMouseEnter={() => setIsAddHovered(true)}
                        onMouseLeave={() => setIsAddHovered(false)}
                        startIcon={
                          isAddHovered ? <AddHoverIcon /> : <AddIcon />
                        }
                        width="54px"
                        height="28px"
                      >
                        Add
                      </OutlineRectangleButton>
                    </Box>
                  </>
                )}
              </Box>
            </Box>
          </Box>
        </Box>

        {/* Footer Buttons */}
        <Box sx={{ display: "flex", justifyContent: "center", mt: 5, mb: 2 }}>
          <OutlineButton
            onClick={handleCloseModal}
            width="100px"
            height="32px"
            sx={{ mr: 2 }}
          >
            Cancel
          </OutlineButton>
          <SolidButton
            onClick={handleInviteUser}
            width="100px"
            height="32px"
            disabled={isSaveDisabled}
          >
            Invite
          </SolidButton>
        </Box>
      </ModalContainer>

      <ModalContainer display={actionModal} modalWidth="500px">
        <Box sx={{ textAlign: "center" }}>
          <Typography
            sx={{
              color: theme.palette.primary.main,
              fontSize: theme.typography.customFontSize[5],
              fontWeight: theme.typography.customFontWeight[3],
              fontFamily: theme.typography.fontFamily,
            }}
          >
            Action denied
          </Typography>

          <Box sx={{ display: "flex", justifyContent: "center" }}>
            <Typography
              sx={{
                color: theme.palette.primary.black,
                fontSize: theme.typography.customFontSize[3],
                fontFamily: theme.typography.fontFamily,
                marginTop: "20px",
              }}
            >
              {errorMessage}
            </Typography>
          </Box>
          <Box sx={{ display: "flex", justifyContent: "center" }}>
            <SolidButton
              sx={{ marginTop: "30px" }}
              onClick={() => setActionModal(false)}
              width="100px"
              height="32px"
            >
              OK
            </SolidButton>
          </Box>
        </Box>
      </ModalContainer>
      <ErrorNotification
        open={errorMessageHandleModal}
        onClose={() => setErrorMessageHandleModal(false)}
        message={errorMessage}
      />
      <GlobalAlert
        open={alertInfo.open}
        severity={alertInfo.severity}
        message={alertInfo.message}
        onClose={() => setAlertInfo((prev) => ({ ...prev, open: false }))}
      />
    </>
  );
};

export default InviteUserModal;
